import { defineStore } from 'pinia'
import { SseService } from '../api'
import { getInitialData } from '@/services/api'
import type { CaseCategory } from '@/services/models/models'
import type { InitResponse } from '@/services/models/response'
import type { OddsSnapshot } from '@/api/models/OddsSnapshot'
// Stores
import { useCasesStore } from './cases'
import { useStatsStore } from './stats'
import { usePromotionalEventsStore } from './promotionalEvents'
import { fireTwitterDepositModalOpened } from '@/utils/twitterPixel'
import { trackMetaInitiateCheckout } from '@/utils/metaPixel'

interface OddsHistoryCache {
  snapshot: OddsSnapshot
  timestamp: number
  caseName: string
}

interface AppState {
  globalLoading: boolean
  initialDataError: boolean
  initialData: InitResponse | null
  caseCategories?: CaseCategory[]
  showBanner: boolean
  showLoginModal: boolean
  showFilters: boolean
  showDrops: boolean
  showDepositModal: boolean
  depositReturnPath: string | null
  showExchangeModal: boolean
  showCasePrizeModal?: boolean
  showPromoModal: boolean
  showDepositStreakModal: boolean
  showEmailVerifiedModal: boolean
  showOddsHistoryModal: boolean
  oddsHistoryCaseId: string | null
  oddsHistoryCache: Record<string, OddsHistoryCache>
  announcement?: string
}

export const useAppStore = defineStore('app', {
  state: (): AppState => ({
    globalLoading: true,
    initialDataError: false,
    initialData: null,
    caseCategories: [],
    // UI
    showBanner: false,
    showFilters: false,
    showDrops: false,
    // Modals
    showLoginModal: false,
    showDepositModal: false,
    depositReturnPath: null,
    showExchangeModal: false,
    showCasePrizeModal: false,
    showPromoModal: false,
    showDepositStreakModal: false,
    showEmailVerifiedModal: false,
    showOddsHistoryModal: false,
    oddsHistoryCaseId: null,
    oddsHistoryCache: {},
    // Anouncement
    announcement: undefined,
  }),
  actions: {
    // Services
    async fetchInitialData(retryOnFailure = true) {
      this.globalLoading = true
      this.initialDataError = false

      const maxAttempts = retryOnFailure ? 2 : 1

      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const response = await getInitialData()

          // Store the full response
          this.initialData = response
          this.caseCategories = response.caseCategories

          const statsStore = useStatsStore()
          const casesStore = useCasesStore()
          const eventsStore = usePromotionalEventsStore()

          // Stats setters
          statsStore.setGeneralStats(response.stats)
          statsStore.setLuckyDrops(response.lucky)
          statsStore.setDrops(response.drops)

          // Cases setters
          casesStore.setCaseCategories(response.caseCategories)
          casesStore.setCases(response.cases)
          casesStore.setVipCases(response.vipCases ?? [])

        // Store the current event - will be accessed by promotionalEvents store
        // Note: promotionalEvents store will get this data via fetchCurrentEventFromInit()

        // Anouncement
          if (response.announcement) {
            this.announcement = response.announcement.text
          }

          this.initialDataError = false
          this.globalLoading = false
          return true
        } catch (error) {
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 700))
            continue
          }

          console.error('Error fetching initial data:', error)
          this.initialDataError = true
        }
      }

      this.globalLoading = false
      return false
    },
    async initializeSse() {
      try {
        await SseService.getListen()
      } catch (error) {
        console.error('Error initializing SSE:', error)
      }
    },

    // Setters
    setShowBanner(show: boolean) {
      this.showBanner = show
    },
    setShowLoginModal(show: boolean) {
      this.showLoginModal = show
    },
    setShowFilters(show: boolean) {
      this.showFilters = show
    },
    setShowDrops(show: boolean) {
      this.showDrops = show
    },
    setShowDepositModal(show: boolean) {
      this.showDepositModal = show
      if (show) {
        if (typeof window !== 'undefined') {
          const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
          const isPaymentReturn = window.location.pathname.includes('/deposit/payop/')

          if (!isPaymentReturn) {
            this.depositReturnPath = currentPath
            window.sessionStorage.setItem('Skinwhales:deposit_return_path', currentPath)
          } else {
            this.depositReturnPath =
              window.sessionStorage.getItem('Skinwhales:deposit_return_path') || this.depositReturnPath
          }
        }
        fireTwitterDepositModalOpened({ location: 'home_page' })
        trackMetaInitiateCheckout({ location: 'home_page' })
      }
    },
    setShowExchangeModal(show: boolean) {
      this.showExchangeModal = show
    },
    setShowCasePrizeModal(show: boolean) {
      this.showCasePrizeModal = show
    },
    setShowPromoModal(show: boolean) {
      this.showPromoModal = show
    },
    setShowDepositStreakModal(show: boolean) {
      this.showDepositStreakModal = show
    },
    setShowEmailVerifiedModal(show: boolean) {
      this.showEmailVerifiedModal = show
    },
    setShowOddsHistoryModal(show: boolean, caseId?: string) {
      this.showOddsHistoryModal = show
      if (show && caseId) {
        this.oddsHistoryCaseId = caseId
      } else if (!show) {
        this.oddsHistoryCaseId = null
      }
    },
    // Odds history cache helpers
    getCachedOddsSnapshot(caseId: string): OddsSnapshot | null {
      const cached = this.oddsHistoryCache[caseId]
      if (!cached) return null

      // Check if cache is older than 5 minutes (300000ms)
      const now = Date.now()
      const age = now - cached.timestamp
      if (age > 300000) {
        // Cache expired, remove it
        delete this.oddsHistoryCache[caseId]
        return null
      }

      return cached.snapshot
    },
    setCachedOddsSnapshot(caseId: string, snapshot: OddsSnapshot, caseName: string) {
      this.oddsHistoryCache[caseId] = {
        snapshot,
        timestamp: Date.now(),
        caseName,
      }
    },
    clearOddsHistoryCache() {
      this.oddsHistoryCache = {}
    },
    setAnnouncement(message: string) {
      this.announcement = message
    },
  },
})
